import { useContext } from "react";
import { Appointment } from "../../../@types/appointments";
import { WidgetContext } from "./WidgetContainer";
import AppointmentDate from "../../appointments/details/AppointmentDate";
import AppointmentLocation from "../../appointments/details/AppointmentLocation";
import AppointmentMeeting from "../../appointments/details/AppointmentMeeting";
import { isEmpty } from "lodash";
import AppointmentDescription from "../../appointments/details/AppointmentDescription";
import eventImageDefault from "../../../../assets/images/appointments/event_image_default.jpg";
import AppointmentRow, {
  Appointment as AppointmentRowData,
} from "../../widgetbars/shared/AppointmentRow";
import CalendarLinks, {
  WidgetCalendar,
} from "../../widgetbars/shared/CalendarLinks";
import AppointmentParticipateButton from "./AppointmentParticipateButton";

// Everything the card shows besides name and date, and what a widget without
// a stored selection falls back to (Channels::AppointmentsWidget::CARD_FIELDS)
export const CARD_FIELDS = ["image", "location", "meeting", "description"];

// The same for a row of the list display, which carries nothing else
// (Channels::AppointmentsWidget::LIST_FIELDS)
export const LIST_FIELDS = ["location", "meeting"];

type AppointmentsWidgetProps = {
  appointment?: Appointment;
  // "Als Liste anzeigen" (tixxt/core#382): upcoming appointments in the
  // legacy info-block row format instead of the single appointment card
  appointments?: AppointmentRowData[];
  // The appointment's calendars, delivered only when the widget has
  // "Link zum Kalender" switched on (tixxt/core#469, #743)
  calendars?: WidgetCalendar[];
  // Which parts the card shows besides name and date (tixxt/core#473). A
  // payload without the attribute shows everything, as before the selection.
  card_fields?: string[];
  // The same for a row of the list display, out of what such a row shows at all.
  list_fields?: string[];
};

export default function AppointmentsWidget({
  appointment,
  appointments,
  calendars,
  card_fields,
  list_fields,
}: AppointmentsWidgetProps) {
  const { w } = useContext(WidgetContext);
  const shows = (field: string) => !card_fields || card_fields.includes(field);

  if (appointments) {
    // scrolls where the widget height is fixed (channel layout, horizontal
    // bar); the vertical bar grows with the list instead. text-sm like the
    // legacy info block rows, independent of the surrounding context.
    return (
      <div className="widget appointments-widget p-4 h-full overflow-y-auto text-sm">
        <ul className="appointments unstyled divide-y divide-neutral">
          {appointments.length > 0 ? (
            appointments.map((a) => (
              <AppointmentRow key={a.id} appointment={a} fields={list_fields} />
            ))
          ) : (
            <li className="text-muted">
              {I18n.t("js.widgets.appointment.no_upcoming_appointments")}
            </li>
          )}
        </ul>
      </div>
    );
  }

  if (!appointment) return null;

  return (
    <div className="widget appointments-widget p-4 flex flex-col gap-3 overflow-hidden max-h-full">
      {/* everything but the participate button scrolls when the card runs
          out of height, so the button stays at the bottom (tixxt/core#747);
          focusable and named, so a keyboard can scroll it too; the ring sits
          inside, since the card clips anything drawn around its content, and
          reaches a little into the card's padding to keep off the text */}
      <div
        className="grow min-h-0 -m-1 p-1 flex flex-col gap-3 overflow-y-auto rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        tabIndex={0}
        role="region"
        aria-label={appointment.name}
        data-testid="appointment-card-content"
      >
        {/* keeps its height, so the scrolling area grows instead of the
            header row squeezing its lines together */}
        <div className="flex gap-8 shrink-0 overflow-hidden">
          {w > 1 && shows("image") ? (
            <div className="overflow-hidden rounded-full self-start shrink-0 hidden sm:block">
              <img
                className="object-cover object-center h-36 w-36"
                src={appointment.event_image?.large_thumb || eventImageDefault}
                alt={""}
                aria-hidden
              />
            </div>
          ) : null}
          <div className="grow min-w-0 flex flex-col gap-4 text-sm">
            {/* the calendars belong to the title, so they sit tight under it
                instead of at the column's own spacing */}
            <div className="flex flex-col gap-1">
              {/* clamped to two lines, so a long name cannot crowd out the rest
                  of the card; the full one comes on hover */}
              <h4
                className={`text-lg font-bold break-words line-clamp-2 ${
                  appointment.canceled ? "line-through" : ""
                }`}
              >
                <a
                  href={`/appointments/${appointment.id}`}
                  title={appointment.name}
                >
                  {appointment.name}
                </a>
              </h4>
              {appointment.canceled && (
                <p className="text-danger font-semibold">
                  {I18n.t("js.calendars.appointment.canceled")}
                </p>
              )}
              {calendars && calendars.length > 0 && (
                <CalendarLinks calendars={calendars} />
              )}
            </div>
            <AppointmentDate
              date={appointment.date}
              isRecurring={false} // @todo:
              recurrence_pattern={appointment.recurrence_pattern}
              next_occurrence_date={appointment.next_occurrence_date}
              next_occurrence_url={appointment.next_occurrence_url}
            />
            {shows("location") && (
              <AppointmentLocation
                {...appointment.location}
                showMap={false}
                compact
              />
            )}
            {shows("meeting") &&
              !appointment.canceled &&
              appointment.meeting?.join_url && (
                <AppointmentMeeting
                  date={appointment.date}
                  meeting={appointment.meeting}
                  appointmentName={appointment.name}
                  canStartMeeting={appointment.can.start_meeting}
                  appointmentId={appointment.id}
                  // only the narrowest card needs the stacked layout; wider ones
                  // keep the side by side arrangement they had
                  compact={w === 1}
                />
              )}
          </div>
        </div>
        {shows("description") && (
          <AppointmentDescription
            description={
              appointment.description_rendered || appointment.description || ""
            }
            isRendered={!isEmpty(appointment.description_rendered)}
            translated={false}
          />
        )}
      </div>
      <AppointmentParticipateButton appointment={appointment} />
    </div>
  );
}
